import {
  CalendarDays,
  CalendarRange,
  Handshake,
  House,
  LayoutGrid,
  ListChecks,
  Palette,
  Radar,
  Users,
  type LucideIcon,
} from "lucide-react";
import { matchPath } from "react-router";

import { HUB_ENTRIES } from "@/components/fretum/common/navEntries";
import { MARKET_BASE, MARKET_TABS } from "@/components/market-intel/routes";

/**
 * Desktop sidebar navigation (pure data + matching helpers, no React).
 * Sub-items that narrow a list encode their filter in the URL exactly like
 * ra-core's list does (`?filter=<json>`), so the list opens pre-filtered.
 */

export type DeskNavLabel =
  { key: string; args?: Record<string, unknown> } | { text: string };

export interface DeskNavLeaf {
  id: string;
  to: string;
  label: DeskNavLabel;
  /** List filter applied through the URL. */
  filter?: Record<string, unknown>;
}

export interface DeskNavItem {
  id: string;
  to: string;
  label: DeskNavLabel;
  icon: LucideIcon;
  /** Route patterns (react-router) that make the item active. */
  match: string[];
  /** The icon reads well filled with currentColor when active. */
  fillWhenActive?: boolean;
  /** Shows the due-tasks counter. */
  badge?: "tasks";
  children?: DeskNavLeaf[];
}

export interface DeskNavConfig {
  noteStatuses: { value: string; label: string }[];
  dealStages: { value: string; label: string }[];
  dealPipelineStatuses: string[];
}

const plural = (key: string): DeskNavLabel => ({
  key,
  args: { smart_count: 2 },
});

/** "(a,b,c)": the PostgREST `@in` value, also understood by FakeRest. */
const inList = (values: string[]) => `(${values.join(",")})`;

const statusLeaf = (
  config: DeskNavConfig,
  value: string,
): DeskNavLeaf | null => {
  const status = config.noteStatuses.find((entry) => entry.value === value);
  return status
    ? {
        id: `contacts-${value}`,
        to: "/contacts",
        label: { text: status.label },
        filter: { status: value },
      }
    : null;
};

export const buildDeskNav = (config: DeskNavConfig): DeskNavItem[] => {
  const stageValues = config.dealStages.map((stage) => stage.value);
  const wonStages = config.dealPipelineStatuses.filter((stage) =>
    stageValues.includes(stage),
  );
  const hasLost = stageValues.includes("lost");

  const contactLeaves: DeskNavLeaf[] = [
    {
      id: "contacts-all",
      to: "/contacts",
      label: { key: "desk.shell.nav.all" },
    },
    ...[statusLeaf(config, "hot"), statusLeaf(config, "in-contract")].filter(
      (leaf): leaf is DeskNavLeaf => leaf !== null,
    ),
    {
      id: "companies",
      to: "/companies",
      label: plural("resources.companies.name"),
    },
    {
      // Latest contact notes, first built for phones (mobile/MobileMessages).
      id: "contacts-messages",
      to: "/mensajes",
      label: { key: "crm.mobile.nav_messages" },
    },
  ];

  const dealLeaves: DeskNavLeaf[] = [
    {
      id: "deals-pipeline",
      to: "/deals",
      label: { key: "desk.shell.nav.pipeline" },
    },
    ...(wonStages.length
      ? [
          {
            id: "deals-won",
            to: "/deals",
            label: { key: "desk.shell.nav.won" },
            filter: { "stage@in": inList(wonStages) },
          },
        ]
      : []),
    ...(hasLost
      ? [
          {
            id: "deals-lost",
            to: "/deals",
            label: { key: "desk.shell.nav.lost" },
            filter: { stage: "lost" },
          },
        ]
      : []),
    {
      // Open pipeline by stage, first built for phones (mobile/MobileReports).
      id: "deals-reports",
      to: "/informes",
      label: { key: "crm.mobile.nav_reports" },
    },
  ];

  return [
    {
      id: "home",
      to: "/",
      label: { key: "ra.page.dashboard" },
      icon: House,
      match: ["/"],
      fillWhenActive: true,
    },
    {
      id: "contacts",
      to: "/contacts",
      label: plural("resources.contacts.name"),
      icon: Users,
      match: ["/contacts/*", "/companies/*", "/mensajes"],
      fillWhenActive: true,
      children: contactLeaves,
    },
    {
      id: "deals",
      to: "/deals",
      label: plural("resources.deals.name"),
      icon: Handshake,
      match: ["/deals/*", "/informes/*"],
      children: dealLeaves,
    },
    {
      id: "tasks",
      to: "/team_tasks",
      label: { key: "hub.nav.tasks" },
      icon: ListChecks,
      match: ["/team_tasks/*", "/tasks/*"],
      badge: "tasks",
      children: [
        {
          id: "tasks-all",
          to: "/team_tasks",
          label: { key: "desk.shell.nav.all_f" },
        },
        {
          id: "tasks-pending",
          to: "/team_tasks",
          label: { key: "desk.shell.nav.pending" },
          filter: { "status@neq": "done" },
        },
        {
          id: "tasks-done",
          to: "/team_tasks",
          label: { key: "desk.shell.nav.done" },
          filter: { status: "done" },
        },
        {
          // Quick tasks (the `tasks` table): internal and client ones.
          id: "tasks-quick",
          to: "/tasks",
          label: { key: "resources.tasks.kind.nav" },
        },
      ],
    },
    {
      id: "agenda",
      to: "/agenda",
      label: { key: "desk.shell.nav.agenda" },
      icon: CalendarRange,
      match: ["/agenda"],
    },
    {
      id: "calendar",
      to: "/calendar",
      label: { key: "hub.nav.calendar" },
      icon: CalendarDays,
      match: ["/calendar", "/events/*"],
    },
    {
      id: "styles",
      to: "/styles",
      label: { key: "hub.nav.styles" },
      icon: Palette,
      match: ["/styles/*"],
      fillWhenActive: true,
    },
    {
      id: "market",
      to: `${MARKET_BASE}/diario`,
      // Short nav label, the same as the bottom bar and the "Más" tile; the
      // page title keeps the full "Inteligencia de mercado".
      label: { key: "crm.mobile.nav_market" },
      icon: Radar,
      match: [`${MARKET_BASE}/*`],
      children: MARKET_TABS.map((tab) => ({
        id: `market-${tab.path}`,
        to: `${MARKET_BASE}/${tab.path}`,
        label: { key: `market.tabs.${tab.id}` },
      })),
    },
    {
      id: "hub",
      to: HUB_ENTRIES[0].to,
      label: { key: "hub.nav.more" },
      icon: LayoutGrid,
      match: HUB_ENTRIES.map((entry) => `${entry.to}/*`),
      fillWhenActive: true,
      children: HUB_ENTRIES.map((entry) => ({
        id: `hub-${entry.to.slice(1)}`,
        to: entry.to,
        label: plural(entry.label),
      })),
    },
  ];
};

/** URL of a nav leaf, with its list filter encoded the ra-core way. */
export const leafHref = (leaf: Pick<DeskNavLeaf, "to" | "filter">): string =>
  leaf.filter
    ? `${leaf.to}?filter=${encodeURIComponent(JSON.stringify(leaf.filter))}`
    : leaf.to;

/** The `filter` object of a location search string ({} when absent/invalid). */
export const parseListFilter = (search: string): Record<string, unknown> => {
  const raw = new URLSearchParams(search).get("filter");
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
};

const sameFilter = (
  left: Record<string, unknown>,
  right: Record<string, unknown>,
) => {
  const leftKeys = Object.keys(left);
  return (
    leftKeys.length === Object.keys(right).length &&
    leftKeys.every(
      (key) => JSON.stringify(left[key]) === JSON.stringify(right[key]),
    )
  );
};

const onPath = (to: string, pathname: string) =>
  !!matchPath(`${to}/*`, pathname);

export const isItemActive = (item: DeskNavItem, pathname: string): boolean =>
  item.match.some((pattern) => !!matchPath(pattern, pathname));

/**
 * The active leaf of a group: the one whose path and filter match the URL;
 * failing that, the unfiltered leaf of the current path (e.g. "All" on a
 * contact's show page). Null when the URL carries a filter no leaf offers.
 */
export const activeLeafId = (
  leaves: DeskNavLeaf[],
  pathname: string,
  search: string,
): string | null => {
  const onCurrentPath = leaves.filter((leaf) => onPath(leaf.to, pathname));
  if (!onCurrentPath.length) return null;
  const filter = parseListFilter(search);
  const exact = onCurrentPath.find((leaf) =>
    sameFilter(leaf.filter ?? {}, filter),
  );
  if (exact) return exact.id;
  const isListPage = !!matchPath(onCurrentPath[0].to, pathname);
  if (isListPage && Object.keys(filter).length) return null;
  return onCurrentPath.find((leaf) => !leaf.filter)?.id ?? null;
};

/** Every distinct pathname the navigation links to. */
export const navDestinations = (items: DeskNavItem[]): string[] => [
  ...new Set(
    items.flatMap((item) =>
      item.children ? item.children.map((leaf) => leaf.to) : [item.to],
    ),
  ),
];
